import React, { useState } from 'react';
import styled from 'styled-components';
import { 
  Plus, 
  Search, 
  Edit2, 
  Trash2, 
  Eye, 
  Calendar,
  Newspaper,
  X,
  Check,
  Image as ImageIcon,
  Clock
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';

const HeaderActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const SearchBox = styled.div`
  position: relative;
  flex: 1;
  min-width: 250px;
  max-width: 400px;

  svg {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--text-light);
  }

  input {
    width: 100%;
    padding: 0.875rem 1rem 0.875rem 2.75rem;
    border: 2px solid var(--border-light);
    border-radius: var(--radius-md);
    font-size: 0.95rem;

    &:focus {
      outline: none;
      border-color: var(--primary-blue);
    }
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, var(--primary-blue), #004d8f);
  color: var(--white);
  padding: 0.875rem 1.5rem;
  border-radius: var(--radius-md);
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 38, 100, 0.3);
  }
`;

const Table = styled.div`
  background: var(--white);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--border-light);
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 0.5fr;
  padding: 1.25rem 1.5rem;
  background: var(--background-light);
  border-bottom: 2px solid var(--border-light);
  font-weight: 700;
  color: var(--text-dark);
  font-size: 0.9rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 0.5fr;
  padding: 1.25rem 1.5rem;
  align-items: center;
  border-bottom: 1px solid var(--border-light);
  transition: var(--transition);

  &:hover {
    background: var(--background-light);
  }

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const CellTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;

  .image {
    width: 60px;
    height: 45px;
    background: var(--background-light);
    border-radius: var(--radius-md);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--text-light);
    flex-shrink: 0;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .content {
    .title {
      font-weight: 600;
      color: var(--text-dark);
      line-height: 1.4;
      margin-bottom: 0.25rem;
    }

    .category {
      font-size: 0.8rem;
      color: var(--text-light);
    }
  }
`;

const CellDate = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-medium);
  font-size: 0.9rem;

  svg {
    color: var(--text-light);
  }
`;

const CellStatus = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.75rem;
  border-radius: var(--radius-full);
  font-size: 0.8rem;
  font-weight: 600;
  background: ${props => props.$published ? 'rgba(0, 150, 57, 0.1)' : 'rgba(245, 158, 11, 0.1)'};
  color: ${props => props.$published ? 'var(--accent-green)' : '#f59e0b'};
  width: fit-content;
`;

const CellActions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;

  button {
    width: 36px;
    height: 36px;
    border-radius: var(--radius-md);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: var(--transition);

    &:hover {
      transform: scale(1.1);
    }
  }

  .view {
    background: rgba(0, 38, 100, 0.1);
    color: var(--primary-blue);

    &:hover {
      background: var(--primary-blue);
      color: var(--white);
    }
  }

  .edit {
    background: rgba(254, 202, 0, 0.1);
    color: var(--accent-gold-dark);

    &:hover {
      background: var(--accent-gold);
      color: var(--primary-blue-dark);
    }
  }

  .delete {
    background: rgba(200, 16, 46, 0.1);
    color: var(--secondary-red);

    &:hover {
      background: var(--secondary-red);
      color: var(--white);
    }
  }
`;

// Modal
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 2rem;
`;

const Modal = styled.div`
  background: var(--white);
  border-radius: var(--radius-xl);
  width: 100%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: var(--shadow-xl);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  border-bottom: 2px solid var(--border-light);

  h2 {
    font-family: var(--font-secondary);
    font-size: 1.5rem;
    color: var(--text-dark);
  }

  button {
    background: none;
    color: var(--text-light);
    cursor: pointer;

    &:hover {
      color: var(--secondary-red);
    }
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.25rem;

  label {
    display: block;
    font-size: 0.9rem;
    font-weight: 600;
    color: var(--text-dark);
    margin-bottom: 0.5rem;
  }

  input, textarea, select {
    width: 100%;
    padding: 0.875rem 1rem;
    border: 2px solid var(--border-light);
    border-radius: var(--radius-md);
    font-size: 0.95rem;
    transition: var(--transition);

    &:focus {
      outline: none;
      border-color: var(--primary-blue);
    }
  }

  textarea {
    min-height: 200px;
    resize: vertical;
  }
`;

const ImageUpload = styled.div`
  border: 2px dashed var(--border-light);
  border-radius: var(--radius-md);
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: var(--transition);
  background: var(--background-light);

  &:hover {
    border-color: var(--primary-blue);
  }

  svg {
    color: var(--primary-blue);
    margin-bottom: 0.75rem;
  }

  p {
    font-size: 0.95rem;
    color: var(--text-medium);
    margin-bottom: 0.5rem;
  }

  span {
    font-size: 0.8rem;
    color: var(--text-light);
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1.5rem;
  border-top: 2px solid var(--border-light);
`;

const Button = styled.button`
  padding: 0.875rem 1.5rem;
  border-radius: var(--radius-md);
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition);

  &.secondary {
    background: var(--background-light);
    color: var(--text-dark);
    border: 2px solid var(--border-light);

    &:hover {
      border-color: var(--text-light);
    }
  }

  &.primary {
    background: linear-gradient(135deg, var(--primary-blue), #004d8f);
    color: var(--white);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(0, 38, 100, 0.3);
    }
  }
`;

const AdminActualites = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [articles, setArticles] = useState([
    { 
      id: 1, 
      title: 'Atelier de réconciliation à Mbaïbokoum',
      category: 'Médiation',
      date: '2024-12-15', 
      status: 'published',
      excerpt: 'Le Médiateur a facilité un dialogue entre communautés...'
    },
    { 
      id: 2, 
      title: 'Rapport annuel 2024 publié',
      category: 'Publication',
      date: '2024-12-10', 
      status: 'published',
      excerpt: '1245 Réclamations résolues avec un taux de satisfaction...'
    },
    { 
      id: 3, 
      title: 'Partenariat avec le PBF',
      category: 'Coopération',
      date: '2024-12-05', 
      status: 'draft',
      excerpt: 'Signature d\'un accord de coopération avec le Peace Building Fund...'
    },
  ]);

  const handleAdd = () => {
    setEditingArticle(null);
    setIsModalOpen(true);
  };

  const handleEdit = (article) => {
    setEditingArticle(article);
    setIsModalOpen(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Supprimer cet article ')) {
      setArticles(articles.filter(a => a.id !== id));
    }
  };

  const handleSave = (e) => {
    e.preventDefault();
    setIsModalOpen(false);
  };

  const filteredArticles = articles.filter(a => 
    a.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <AdminLayout title="Gestion des Actualités">
      <HeaderActions>
        <SearchBox>
          <Search size={20} />
          <input
            type="text"
            placeholder="Rechercher un article..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchBox>
        <AddButton onClick={handleAdd}>
          <Plus size={20} />
          Nouvel article
        </AddButton>
      </HeaderActions>

      <Table>
        <TableHeader>
          <div>Titre</div>
          <div>Date</div>
          <div>Statut</div>
          <div>Actions</div>
        </TableHeader>

        {filteredArticles.map(article => (
          <TableRow key={article.id}>
            <CellTitle>
              <div className="image">
                <ImageIcon size={24} />
              </div>
              <div className="content">
                <div className="title">{article.title}</div>
                <div className="category">{article.category}</div>
              </div>
            </CellTitle>
            <CellDate>
              <Calendar size={16} />
              {new Date(article.date).toLocaleDateString('fr-FR')}
            </CellDate>
            <CellStatus $published={article.status === 'published'}>
              {article.status === 'published' ? (
                <><Check size={14} /> Publié</>
              ) : (
                <><Clock size={14} /> Brouillon</>
              )}
            </CellStatus>
            <CellActions>
              <button className="view" title="Voir">
                <Eye size={18} />
              </button>
              <button className="edit" title="Modifier" onClick={() => handleEdit(article)}>
                <Edit2 size={18} />
              </button>
              <button className="delete" title="Supprimer" onClick={() => handleDelete(article.id)}>
                <Trash2 size={18} />
              </button>
            </CellActions>
          </TableRow>
        ))}
      </Table>

      {isModalOpen && (
        <ModalOverlay onClick={() => setIsModalOpen(false)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h2>{editingArticle ? 'Modifier l\'article' : 'Nouvel article'}</h2>
              <button onClick={() => setIsModalOpen(false)}>
                <X size={24} />
              </button>
            </ModalHeader>
            <form onSubmit={handleSave}>
              <ModalBody>
                <FormGroup>
                  <label>Titre de l'article</label>
                  <input 
                    type="text" 
                    placeholder="Ex: Atelier de réconciliation..."
                    defaultValue={editingArticle.title || ''}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <label>Catégorie</label>
                  <select defaultValue={editingArticle.category || ''}>
                    <option value="">Sélectionner une catégorie</option>
                    <option value="Médiation">Médiation</option>
                    <option value="Publication">Publication</option>
                    <option value="Coopération">Coopération</option>
                    <option value="?vénement">?vénement</option>
                  </select>
                </FormGroup>

                <FormGroup>
                  <label>Image à la une</label>
                  <ImageUpload>
                    <ImageIcon size={32} />
                    <p>Cliquez pour sélectionner une image</p>
                    <span>JPEG, PNG - Max 5 Mo</span>
                  </ImageUpload>
                </FormGroup>

                <FormGroup>
                  <label>Extrait</label>
                  <input 
                    type="text" 
                    placeholder="Brève description de l'article..."
                    defaultValue={editingArticle.excerpt || ''}
                  />
                </FormGroup>

                <FormGroup>
                  <label>Contenu</label>
                  <textarea 
                    placeholder="Rédigez le contenu de l'article ici..."
                  />
                </FormGroup>

                <FormGroup>
                  <label>Date de publication</label>
                  <input 
                    type="date" 
                    defaultValue={editingArticle.date || new Date().toISOString().split('T')[0]}
                  />
                </FormGroup>

                <FormGroup>
                  <label>Statut</label>
                  <select defaultValue={editingArticle.status || 'draft'}>
                    <option value="draft">Brouillon</option>
                    <option value="published">Publié</option>
                  </select>
                </FormGroup>
              </ModalBody>
              <ModalFooter>
                <Button type="button" className="secondary" onClick={() => setIsModalOpen(false)}>
                  Annuler
                </Button>
                <Button type="submit" className="primary">
                  {editingArticle ? 'Enregistrer' : 'Publier'}
                </Button>
              </ModalFooter>
            </form>
          </Modal>
        </ModalOverlay>
      )}
    </AdminLayout>
  );
};

export default AdminActualites;
